// Cruise Command · Weekly Briefing — shared editorial primitives
// -------------------------------------------------------------
// PO 2026-02-14 · Briefing content/IA pass. Extracted verbatim from
// `BriefingReader.jsx` so the Briefing Landing (`JourneyIntelligence`)
// can render the current briefing inline in the same editorial voice
// as the dedicated reader route. No visual redesign — one source of
// truth for section rendering.
//
// Consumers:
//   • BriefingReader.jsx   (dedicated `briefing:<id>` reader route)
//   • JourneyIntelligence  (Briefing Landing — inline "This Week's
//                           Cruise Intelligence")
//
// Presentation-only; no data fetching, no routing decisions.

import React from "react";
import { ChevronRight } from "lucide-react";
import { useCruise } from "@/context/CruiseContext";
import { ItemThumbnail } from "@/components/briefing/BriefingMedia";
import { WeatherStrip } from "@/components/briefing/BriefingWeather";

const WORKSPACE_ROUTES = {
  finance: "finances",
  finances: "finances",
  dining: "dining",
  excursions: "excursions",
  travelers: "travelers",
  documents: "documents",
  packing: "packing",
};

// ── Since-Last-Week ribbon ──────────────────────────────────
export function DeltaRibbon({ deltas }) {
  if (!deltas || deltas.length === 0) return null;
  return (
    <section
      className="mt-8 rounded-2xl border border-[#C28E42]/25 bg-[#FBF7F0] px-5 py-5 sm:px-6 sm:py-6"
      data-testid="briefing-since-last-week"
    >
      <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#8A5E1E]">
        <span aria-hidden="true">🔄</span>
        <span>Since Last Week</span>
      </div>
      <ul className="mt-4 space-y-3">
        {deltas.map((d, i) => (
          <li
            key={i}
            className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4"
            data-testid={`briefing-delta-${(d.category || "row").toLowerCase()}`}
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8A5E1E] sm:min-w-[110px]">
              {d.label}
            </span>
            <span className="text-[15px] leading-[1.55] text-[#0F172A] font-display">
              {d.summary}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Titled article section (What Changed / What Matters / …) ─
// `enrich(item)` is optional (RC8 · newsletter treatment): it may return
// `{ route, linkLabel, media }` to add a published hero thumbnail and an
// in-app link. Without it the section renders exactly as before.
export function Section({ eyebrow, items, enrich }) {
  const { setRoute } = useCruise();
  if (!items || items.length === 0) return null;
  const slug = eyebrow.toLowerCase().replace(/\s+/g, "-");
  return (
    <section
      className="mt-14"
      data-testid={`briefing-section-${slug}`}
    >
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#C28E42]">
        {eyebrow}
      </div>
      <div className="mt-4 divide-y divide-slate-200/70">
        {items.map((it, i) => {
          const extra = (enrich && enrich(it)) || null;
          return (
            <article key={i} className="py-5 flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
              {extra?.media && <ItemThumbnail media={extra.media} alt={it.label} />}
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-[19px] leading-snug text-[#0F172A] font-semibold">
                  {it.label}
                </h3>
                {it.body && (
                  <p className="mt-2 text-[15px] leading-[1.7] text-[#334155]">
                    {it.body}
                  </p>
                )}
                {extra?.weather && <WeatherStrip entry={extra.weather} />}
                {extra?.route && (
                  <button
                    type="button"
                    onClick={() => setRoute(extra.route)}
                    className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#8A5E1E] hover:text-[#0F172A] transition-colors"
                    data-testid={`briefing-item-link-${slug}-${i}`}
                  >
                    {extra.linkLabel || "Open"}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

// ── For Your Voyage · workspace deep-links ───────────────────
export function ActionsSection({ actions }) {
  const { setRoute } = useCruise();
  if (!actions || actions.length === 0) return null;
  return (
    <section className="mt-14" data-testid="briefing-section-for-your-voyage">
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#C28E42]">
        For Your Voyage
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {actions.map((a, i) => {
          const target = a.workspace && WORKSPACE_ROUTES[a.workspace];
          return (
            <button
              type="button"
              key={i}
              onClick={() => target && setRoute(target)}
              className={`text-left rounded-xl border border-slate-200 bg-white px-5 py-4 transition-colors ${
                target ? "hover:border-[#C28E42] hover:bg-amber-50/40 cursor-pointer" : "cursor-default"
              }`}
              data-testid={`briefing-action-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-[15px] text-[#0F172A]">
                    {a.label}
                  </div>
                  {a.body && (
                    <div className="mt-1.5 text-[13.5px] leading-relaxed text-[#475569]">
                      {a.body}
                    </div>
                  )}
                </div>
                {target && <ChevronRight className="w-4 h-4 text-[#C28E42] shrink-0 mt-0.5" />}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
